import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, dynamicLabel, timelineLabel } from "../lib/api";
import { FALLBACK_PHOTO } from "../lib/ui";
import { useAuth } from "../context/AuthContext";
import { ProfileEditForm } from "../components/ProfileEditForm";

const ProfileView = ({ user, onEdit }) => (
  <>
    <h1 className="font-serif text-4xl" data-testid="profile-name">
      {user.name}
      {user.age ? `, ${user.age}` : ""}
    </h1>
    <p className="mt-2 text-[#8A8A8A]">{user.location}</p>
    <div className="mt-6 flex flex-wrap gap-3">
      {user.relationship_dynamic && (
        <span
          className="bg-[#7A3B3F] text-[#FAF9F6] px-4 py-1.5 text-xs uppercase tracking-[0.15em]"
          data-testid="profile-dynamic-badge"
        >
          {dynamicLabel(user.relationship_dynamic)}
        </span>
      )}
      {user.marriage_timeline && (
        <span className="border border-[#4A5D4E] text-[#4A5D4E] px-4 py-1.5 text-xs uppercase tracking-[0.15em]">
          Marriage: {timelineLabel(user.marriage_timeline)}
        </span>
      )}
    </div>
    <p className="mt-8 text-[#5C5C5C] leading-relaxed text-lg">{user.bio}</p>
    {user.values?.length > 0 && (
      <div className="mt-6 flex flex-wrap gap-2">
        {user.values.map((v) => (
          <span key={v} className="text-xs capitalize text-[#8A8A8A] border border-[#E5E0D8] px-3 py-1">
            {v}
          </span>
        ))}
      </div>
    )}
    <button
      data-testid="profile-edit-button"
      onClick={onEdit}
      className="mt-10 border border-[#1A1A1A] px-8 py-3 hover:bg-[#1A1A1A] hover:text-[#FAF9F6] transition-colors duration-300"
    >
      Edit profile
    </button>
  </>
);

const buildEditInitial = (user) => ({
  age: user.age || "",
  location: user.location || "",
  bio: user.bio || "",
  relationship_dynamic: user.relationship_dynamic || "",
  marriage_timeline: user.marriage_timeline || "",
  values: user.values || [],
  photo_url: user.photo_url || "",
});

export default function ProfilePage() {
  const { user, setUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  if (!user) return null;

  const save = async (form) => {
    setSaving(true);
    try {
      const { data } = await api.put("/profile", { ...form, age: parseInt(form.age, 10) });
      setUser(data);
      setEditing(false);
      toast.success("Profile updated");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 sm:px-12 py-16" data-testid="profile-page">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#7A3B3F]">Your profile</p>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="mt-8 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 bg-[#F3F0EA] border border-[#E5E0D8]"
      >
        <div className="md:col-span-5 relative min-h-[320px]">
          <img
            src={user.photo_url || FALLBACK_PHOTO}
            alt={user.name}
            className="absolute inset-0 w-full h-full object-cover"
          />
        </div>
        <div className="md:col-span-7 p-8 sm:p-12">
          {!editing ? (
            <ProfileView user={user} onEdit={() => setEditing(true)} />
          ) : (
            <ProfileEditForm
              initial={buildEditInitial(user)}
              onSave={save}
              onCancel={() => setEditing(false)}
              saving={saving}
            />
          )}
        </div>
      </motion.div>
    </div>
  );
}
